import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, Trash2, FileText, Sparkles, Loader2, Euro, FileCode2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATS = [
  { v: "fattura", l: "Fattura" },
  { v: "ricevuta", l: "Ricevuta" },
  { v: "scontrino", l: "Scontrino" },
  { v: "certificato", l: "Certificato" },
  { v: "contratto", l: "Contratto" },
  { v: "altro", l: "Altro" },
];

const CATEGORIE_AI_MAP = {
  vitto: "ricevuta", trasporti: "ricevuta", forniture: "fattura",
  attrezzature: "fattura", servizi: "fattura", utenze: "fattura",
  salute: "ricevuta", carburante: "ricevuta", abbigliamento: "ricevuta",
};

export default function Vault() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [cat, setCat] = useState("fattura");
  const [notes, setNotes] = useState("");
  const [fileData, setFileData] = useState("");
  const [fileType, setFileType] = useState("");
  const [parsedData, setParsedData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [xmlBatch, setXmlBatch] = useState(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setName(""); setCat("fattura"); setNotes(""); setFileData(""); setFileType("");
    setParsedData(null); setOcrLoading(false);
  };

  const runOcr = async (base64, mimeType) => {
    if (!mimeType.startsWith("image/")) return;
    setOcrLoading(true);
    try {
      const r = await api.post("/documents/ocr", { image_base64: base64 });
      const p = r.data;
      setParsedData(p);
      if (p.fornitore) setName(`${p.fornitore}${p.data ? " - " + p.data : ""}`);
      if (p.tipo && CATS.find((c) => c.v === p.tipo)) setCat(p.tipo);
      else if (p.categoria_suggerita && CATEGORIE_AI_MAP[p.categoria_suggerita]) {
        setCat(CATEGORIE_AI_MAP[p.categoria_suggerita]);
      }
      const noteParts = [];
      if (p.totale) noteParts.push(`Totale: € ${p.totale}`);
      if (p.iva) noteParts.push(`IVA: € ${p.iva} (${p.aliquota_iva || "?"}%)`);
      if (p.numero_documento) noteParts.push(`N° ${p.numero_documento}`);
      if (p.descrizione) noteParts.push(p.descrizione);
      if (noteParts.length) setNotes(noteParts.join(" · "));
      toast.success("Dati estratti dall'AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore analisi AI");
    } finally { setOcrLoading(false); }
  };

  const importXmlFiles = async (files) => {
    if (!files.length) return;
    setXmlBatch({ total: files.length, done: 0, current: "" });
    let imported = 0, failed = 0;
    for (const f of files) {
      setXmlBatch((s) => ({ ...s, current: f.name }));
      try {
        const text = await f.text();
        const r = await api.post("/documents/parse-fatturapa", { xml_text: text });
        const p = r.data;
        const noteParts = [];
        if (p.totale) noteParts.push(`Totale: € ${p.totale}`);
        if (p.iva) noteParts.push(`IVA: € ${p.iva} (${p.aliquota_iva || "?"}%)`);
        if (p.numero_documento) noteParts.push(`N° ${p.numero_documento}`);
        if (p.descrizione) noteParts.push(p.descrizione);
        await api.post("/documents", {
          name: `${p.fornitore || f.name}${p.data ? " - " + p.data : ""}`,
          category: "fattura",
          notes: noteParts.join(" · "),
          file_data: "",
          file_type: "application/xml",
          parsed_data: p,
        });
        imported++;
      } catch (e) {
        failed++;
      }
      setXmlBatch((s) => ({ ...s, done: s.done + 1 }));
    }
    setXmlBatch(null);
    toast.success(`${imported} fatture importate${failed ? ` (${failed} errori)` : ""}`);
    load();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2MB per file"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setFileData(dataUrl);
      setFileType(f.type);
      if (!name) setName(f.name);
      // Auto-run OCR for images
      if (f.type.startsWith("image/")) {
        const b64 = dataUrl.split(",")[1];
        runOcr(b64, f.type);
      }
    };
    reader.readAsDataURL(f);
  };

  const create = async () => {
    if (!name) { toast.error("Nome obbligatorio"); return; }
    try {
      await api.post("/documents", {
        name, category: cat, notes,
        file_data: fileData, file_type: fileType,
        parsed_data: parsedData,
      });
      toast.success("Documento salvato");
      setOpen(false); resetForm(); load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); load(); toast.success("Eliminato"); };

  return (
    <div className="space-y-6" data-testid="vault-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Archivio con OCR AI
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">I tuoi documenti, letti dall'AI.</h1>
          <p className="text-muted-foreground text-sm mt-2">Carica una foto di fattura o scontrino: l'AI estrae automaticamente importo, IVA, data e fornitore. Oppure importa gli XML delle fatture elettroniche.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label className="cursor-pointer">
            <input type="file" accept=".xml" multiple onChange={(e) => importXmlFiles(Array.from(e.target.files || []))} className="hidden" data-testid="vault-xml-input" />
            <Button asChild variant="outline" className="rounded-full h-12 px-6"><span><FileCode2 className="w-4 h-4 mr-2" /> Importa XML</span></Button>
          </label>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-12 px-6" data-testid="vault-add-button"><Upload className="w-4 h-4 mr-2" /> Nuovo documento</Button>
            </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Aggiungi documento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>File (immagine per OCR automatico, max 2MB)</Label>
                <Input type="file" accept="image/*,.pdf" onChange={onFile} className="h-12 rounded-xl mt-1.5" data-testid="vault-file-input" />
                <p className="text-xs text-muted-foreground mt-1">JPG/PNG per estrazione automatica dati.</p>
              </div>

              {ocrLoading && (
                <Card className="p-4 rounded-xl bg-accent/40 border-primary/20 flex items-center gap-3" data-testid="vault-ocr-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <div>
                    <div className="text-sm font-semibold">Analisi AI in corso...</div>
                    <div className="text-xs text-muted-foreground">Estrazione automatica dei dati</div>
                  </div>
                </Card>
              )}

              {parsedData && !ocrLoading && (
                <Card className="p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="vault-ocr-result">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <div className="text-sm font-heading font-semibold">Dati estratti dall'AI</div>
                    <Badge variant="secondary" className="rounded-full text-[10px] ml-auto">Modificabile</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {parsedData.fornitore && <div><span className="text-muted-foreground">Fornitore:</span> <strong>{parsedData.fornitore}</strong></div>}
                    {parsedData.data && <div><span className="text-muted-foreground">Data:</span> <strong>{parsedData.data}</strong></div>}
                    {parsedData.totale != null && <div><span className="text-muted-foreground">Totale:</span> <strong className="text-primary">€ {parsedData.totale}</strong></div>}
                    {parsedData.iva != null && <div><span className="text-muted-foreground">IVA:</span> <strong>€ {parsedData.iva} ({parsedData.aliquota_iva}%)</strong></div>}
                    {parsedData.partita_iva && <div className="col-span-2"><span className="text-muted-foreground">P.IVA:</span> <strong>{parsedData.partita_iva}</strong></div>}
                    {parsedData.categoria_suggerita && <div className="col-span-2"><span className="text-muted-foreground">Categoria AI:</span> <Badge variant="outline" className="rounded-full text-[10px] ml-1">{parsedData.categoria_suggerita}</Badge></div>}
                  </div>
                </Card>
              )}

              <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="vault-name-input" /></div>
              <div><Label>Categoria</Label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="vault-cat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Note</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl mt-1.5" data-testid="vault-notes-input" /></div>
              <Button onClick={create} className="rounded-full w-full h-12" data-testid="vault-save-button">Salva</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {xmlBatch && (
        <Card className="p-4 rounded-2xl border-2 border-primary/20 bg-primary/5 flex items-center gap-3" data-testid="vault-xml-progress">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <div className="flex-1">
            <div className="text-sm font-semibold">Import XML in corso... ({xmlBatch.done}/{xmlBatch.total})</div>
            <div className="text-xs text-muted-foreground truncate">{xmlBatch.current}</div>
          </div>
        </Card>
      )}

      {docs.length === 0 ? (
        <Card className="p-12 rounded-2xl border-dashed border-2 text-center" data-testid="vault-empty">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold text-xl mb-1">Archivio vuoto</div>
          <div className="text-muted-foreground text-sm">Carica una foto di uno scontrino o fattura per iniziare.</div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <Card key={d.id} className="p-5 rounded-2xl border" data-testid={`doc-item-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><FileText className="w-5 h-5 text-primary" /></div>
                <Button variant="ghost" size="icon" onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="font-semibold truncate">{d.name}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant="secondary" className="rounded-full">{d.category}</Badge>
                {d.parsed_data?.totale != null && (
                  <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10 border-primary/20">
                    <Euro className="w-3 h-3 mr-0.5" /> {d.parsed_data.totale}
                  </Badge>
                )}
              </div>
              {d.parsed_data && (
                <div className="mt-3 p-2 rounded-lg bg-accent/40 text-xs space-y-0.5">
                  {d.parsed_data.fornitore && <div className="truncate"><strong>{d.parsed_data.fornitore}</strong></div>}
                  {d.parsed_data.data && <div className="text-muted-foreground">{d.parsed_data.data}</div>}
                  {d.parsed_data.iva != null && <div className="text-muted-foreground">IVA € {d.parsed_data.iva} ({d.parsed_data.aliquota_iva}%)</div>}
                </div>
              )}
              {d.notes && !d.parsed_data && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{d.notes}</p>}
              <div className="text-xs text-muted-foreground mt-3">{new Date(d.created_at).toLocaleDateString("it-IT")}</div>
              {d.file_data && (
                <a href={d.file_data} download={d.name} className="text-xs text-primary font-semibold mt-2 inline-block" data-testid={`doc-download-${d.id}`}>Scarica →</a>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
